/**
 * Section Context for Block Components
 * 
 * Provides section-level context (sectionId) to block components,
 * enabling instance-scoped token resolution. Each block instance
 * on a page gets a unique sectionId, and its content tokens are
 * automatically namespaced under that ID.
 * 
 * Standard components can ignore this context entirely -- it only
 * affects blocks that use the useBlockToken/useBlockSlotGroup hooks.
 */

import { createContext, useContext, type ReactNode, type ComponentType, type CSSProperties } from 'react';
import type { ContentTokenOptions, SlotGroupResult } from './context';
import { useContentToken, useSlotGroup, useCollectionBindingState, useRegistry, type CollectionBindingOptions } from './context';
import { EditableToken, type EditableTokenProps } from './editable-token';
import type { SlotGroupDefinition } from '../types';

// ============================================================================
// Section Context
// ============================================================================

export interface SectionContextValue {
  /** The unique section instance ID (e.g., 'tour-scheduler') */
  sectionId: string;
}

const SectionContext = createContext<SectionContextValue | null>(null);

/**
 * Provider that establishes the section context for block components.
 * The runtime wraps each block section in this provider.
 */
export function SectionContextProvider({
  sectionId,
  children,
}: {
  sectionId: string;
  children: ReactNode;
}) {
  return (
    <SectionContext.Provider value={{ sectionId }}>
      {children}
    </SectionContext.Provider>
  );
}

/**
 * Hook to access the current section context.
 * Returns null if not inside a SectionContextProvider (i.e., in a standard component).
 */
export function useSectionContext(): SectionContextValue | null {
  return useContext(SectionContext);
}

// ============================================================================
// Block-Scoped Hooks
// ============================================================================

/**
 * Hook to resolve a content token scoped to the current block instance.
 * 
 * Automatically prefixes the token name with the section ID, so block
 * components can use simple local names (e.g., 'url') that resolve to
 * instance-scoped paths (e.g., 'tour-scheduler.url').
 * 
 * @param localName - The local token name (e.g., 'url', 'title')
 * @param options - Content token options (defaultField, defaultValue)
 * @returns The resolved token value or null
 * 
 * @example
 * function IframeEmbed() {
 *   const url = useBlockToken('url', {
 *     defaultField: 'custom_eliseai_tour_scheduler_url',
 *   });
 *   const height = useBlockToken('height', { defaultValue: '600px' });
 *   return <iframe src={url} height={height} />;
 * }
 */
export function useBlockToken(
  localName: string,
  options: ContentTokenOptions = {}
): string | null {
  const sectionCtx = useSectionContext();
  const tokenPath = sectionCtx
    ? `${sectionCtx.sectionId}.${localName}`
    : localName;
  return useContentToken(tokenPath, options);
}

/**
 * Hook to resolve a slot group scoped to the current block instance.
 * 
 * @param localGroupName - The local slot group name
 * @param definition - The slot group definition
 * @returns Object with resolved values for each field
 */
export function useBlockSlotGroup(
  localGroupName: string,
  definition: SlotGroupDefinition
): SlotGroupResult {
  const sectionCtx = useSectionContext();
  const groupPath = sectionCtx
    ? `${sectionCtx.sectionId}.${localGroupName}`
    : localGroupName;
  return useSlotGroup(groupPath, definition);
}

/** Scopes the path to the current section, as useBlockCollectionBinding does. */
export function useBlockCollectionBindingState<T = unknown>(
  localBindingName: string,
  options: CollectionBindingOptions,
) {
  const sectionCtx = useSectionContext();
  const bindingPath = sectionCtx
    ? `${sectionCtx.sectionId}.${localBindingName}`
    : localBindingName;
  return useCollectionBindingState<T>(bindingPath, options);
}

/**
 * Hook to resolve a collection binding scoped to the current block instance.
 * 
 * @param localBindingName - The local binding name
 * @param options - Collection binding options
 * @returns Array of resolved collection items
 */
export function useBlockCollectionBinding<T = unknown>(
  localBindingName: string,
  options: CollectionBindingOptions,
): T[] {
  return useBlockCollectionBindingState<T>(localBindingName, options).items;
}

// ============================================================================
// Editable Block Token
// ============================================================================

export interface EditableBlockTokenProps {
  /** Local token name within the block (e.g., 'form_header_text') */
  localName: string;
  /** Token type for visual styling hints */
  type?: EditableTokenProps['type'];
  /** Display label for the token */
  label?: string;
  /** Token description */
  description?: string;
  /** Default property field (for field mode) */
  defaultField?: string;
  /** Default value (for custom mode) */
  defaultValue?: string;
  /** Children to render */
  children: ReactNode;
  /** Additional class name */
  className?: string;
  /** Additional inline styles */
  style?: CSSProperties;
  /** HTML tag to render */
  as?: EditableTokenProps['as'];
}

/**
 * EditableToken wrapper for block components.
 * 
 * Automatically prefixes the token path with the current section context ID,
 * so block components can use simple local names while the full path is
 * resolved correctly for the editor.
 * 
 * @example
 * function MyBlock() {
 *   const title = useBlockToken('title', { defaultValue: 'Hello' });
 *   return (
 *     <EditableBlockToken localName="title" defaultValue="Hello" as="h2">
 *       {title}
 *     </EditableBlockToken>
 *   );
 * }
 */
export function EditableBlockToken({
  localName,
  type = 'text',
  label,
  description,
  defaultField,
  defaultValue,
  children,
  className,
  style,
  as,
}: EditableBlockTokenProps) {
  const sectionCtx = useSectionContext();
  const fullPath = sectionCtx
    ? `${sectionCtx.sectionId}.${localName}`
    : localName;

  return (
    <EditableToken
      path={fullPath}
      type={type}
      label={label}
      description={description}
      defaultField={defaultField}
      defaultValue={defaultValue}
      className={className}
      style={style}
      as={as}
    >
      {children}
    </EditableToken>
  );
}

// ============================================================================
// Block Slot Hook
// ============================================================================

export interface BlockSlotResult {
  /** The React component for the resolved block */
  BlockComponent: ComponentType<{ sectionId: string }>;
  /** The block catalog ID (e.g., 'multihub-lead-form') */
  blockId: string;
  /** The derived sectionId for scoping the block's tokens */
  sectionId: string;
}

/**
 * Hook that resolves a block slot from a content token.
 * 
 * Reads the block ID from the specified content token path, looks up
 * the corresponding block component in the registry, and returns
 * everything needed to render the block within a SectionContextProvider.
 * 
 * The sectionId is derived from the tokenPath by stripping a trailing
 * `_block` suffix (if present) and replacing dots with dashes. For example,
 * tokenPath `hero.form_block` produces sectionId `hero.form`.
 * 
 * @param tokenPath - The content token path storing the block ID
 * @param options - Standard content token options (defaultValue sets the default block ID)
 * @returns BlockSlotResult with the component, blockId, and sectionId, or null if no block is set
 * 
 * @example
 * function Hero() {
 *   const formSlot = useBlockSlot('hero.form_block', {
 *     defaultValue: 'multihub-lead-form',
 *   });
 * 
 *   return (
 *     <div>
 *       {formSlot ? (
 *         <SectionContextProvider sectionId={formSlot.sectionId}>
 *           <formSlot.BlockComponent sectionId={formSlot.sectionId} />
 *         </SectionContextProvider>
 *       ) : (
 *         <div>No block selected</div>
 *       )}
 *     </div>
 *   );
 * }
 */
export function useBlockSlot(
  tokenPath: string,
  options: ContentTokenOptions = {}
): BlockSlotResult | null {
  const blockId = useContentToken(tokenPath, options);
  const registry = useRegistry();

  if (!blockId) return null;

  // Look up the block component in the registry (blocks are registered as `block:${id}`)
  const component = registry[`block:${blockId}`];
  if (!component) return null;

  // Derive sectionId from tokenPath:
  // 'hero.form_block' → 'hero.form'
  const sectionId = tokenPath.replace(/_block$/, '');

  return {
    BlockComponent: component as ComponentType<{ sectionId: string }>,
    blockId,
    sectionId,
  };
}
